import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SlidersHorizontal, X, RefreshCw } from 'lucide-react';
import { resolveContactIdentity } from '../../../api/contact';
import ContactBuyerPreferencesEditor from './ContactBuyerPreferencesEditor';
import BuyerStatusActions from './BuyerStatusActions';

/**
 * Buying Preferences editor in a modal, opened from any list where a person is
 * shown by identity rather than a known Contact id (enquiries, inspection
 * attendees, property buyers…). Resolves (get-or-create) a CRM Contact from the
 * passed identity, then mounts the portal-mirror `ContactBuyerPreferencesEditor`,
 * which writes through to the buyer's portal preferences and our buyer-trend
 * stats. Pass any of {contactId, email, name, phone, buyerId}.
 *
 * Rendered conditionally by the parent: mount it only when `identity` is set,
 * and clear that state in `onClose`.
 */
const BuyerPreferencesModal = ({ identity, title, onClose, onSaved }) => {
  const [resolving, setResolving] = useState(false);
  const [contactId, setContactId] = useState(identity?.contactId || null);
  const statusRef = useRef(null);
  const statusBuyer = useMemo(
    () => ({ ...identity, contactId: contactId ?? identity?.contactId }),
    [identity, contactId],
  );

  useEffect(() => {
    let active = true;
    // Already have a Contact id — nothing to resolve.
    if (identity?.contactId) {
      setContactId(identity.contactId);
      return undefined;
    }
    (async () => {
      setResolving(true);
      try {
        const res = await resolveContactIdentity(identity);
        if (active) setContactId(res.data?.contact_id || null);
      } catch (e) {
        // Editor shows a "couldn't open" state if this stays null.
      } finally {
        if (active) setResolving(false);
      }
    })();
    return () => { active = false; };
  }, [identity]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="w-4 h-4 text-[#3a99a3] shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              Buying preferences{title ? ` · ${title}` : ''}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Status + quick-actions bar on every buying-preferences surface, not
            just Who to Call (Christian Gravias, CRM-2026-00097). */}
        {identity && (
          <div className="flex-shrink-0">
            <BuyerStatusActions ref={statusRef} buyer={statusBuyer} />
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {resolving ? (
            <div className="flex items-center gap-2 justify-center py-16 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Opening preferences…
            </div>
          ) : contactId ? (
            <ContactBuyerPreferencesEditor
              contactId={contactId}
              beforeSave={() => statusRef.current?.savePending() ?? true}
              onSaved={(snapshot) => {
                onSaved?.(snapshot);
                onClose?.();
              }}
            />
          ) : (
            <div className="py-16 text-center text-sm text-gray-500">
              Couldn&apos;t open this buyer&apos;s preferences. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerPreferencesModal;
